import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Outlet, NavLink, useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { toast } from 'sonner';
import { Bell, Sparkles, Users, LogOut, Menu, Ticket, Settings, Layers, ChevronLeft } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/lib/api';
import { getTenantIdFromJWT, getRoleIdFromJWT } from '@/lib/auth/jwt';
import {
  SPOOF_CHANGED_EVENT,
  SPOOF_JWT_KEY,
  SPOOF_LABEL_KEY,
  clearSpoofLocalStorage,
  dispatchSpoofChanged,
  fetchPagesForRole,
  getEffectiveToken,
  getSpoofUserLabel,
  isSpoofing,
} from '@/lib/auth/spoof';
import { icons } from 'lucide-react';
import { CustomIcons } from '@/components/page-builder/NewCustomIcons';
import { FollowUpIcon, WIPTicketIcon, RoutingSettingsIcon, LeadScoreIcon, AnalyticsIcon } from '@/components/icons/CustomIcons';
import { SparkySidebarButton } from '@/components/chatbot/ChatWidget';
import { LeadCalledBackNotificationsMenu } from '@/features/lead-called-back-notification/LeadCalledBackNotificationsMenu';
import { clearRegisteredAllLeadsPath, registerAllLeadsPath } from '@/lib/realtime/openLeadBus';
import { useShiftAutoLogout } from '@/hooks/useShiftAutoLogout';
import { ShiftEndedOverlay } from '@/components/ShiftEndedOverlay';
import { useLeadNavLocked } from '@/lib/realtime/shiftCallActivityBus';
import { buildSidebarNav } from '@/lib/sidebar/sidebarGroups';
import { SidebarGroupFlyout } from './SidebarGroupFlyout';

type CustomIconRow = { name: string; svg_content: string };

const navigationIconMap: Record<string, JSX.Element> = {
    "lead groups": <Users className="h-4 w-4" />,
    "lead assignment": <Users className="h-4 w-4" />,
    "user hierarchy": <Users className="h-4 w-4" />,
    "analytics": <AnalyticsIcon />,
    "pending tickets": <Ticket className="h-4 w-4" />,
    "wip tickets": <WIPTicketIcon className="h-4 w-4" />,
    "all support tickets": <Layers className="h-4 w-4" />,
    "settings": <Settings className="h-4 w-4" />,
    "notifications": <Bell className="h-4 w-4" />,
    // Legacy mappings for backwards compatibility
    "pending leads": <Sparkles className="h-4 w-4" />,
    "all leads": <Users className="h-4 w-4" />,
    "follow up leads": <FollowUpIcon />,
    "routing settings": <RoutingSettingsIcon />,
    "lead score": <LeadScoreIcon />,
  };

const DynamicSidebarIcon = ({
  iconName,
  customIcons = [],
}: {
  iconName: string;
  customIcons?: CustomIconRow[];
}) => {
  
  // 1. Check if it's an uploaded custom icon from the database
  const uploadedIcon = customIcons.find(icon => icon.name.toLowerCase() === iconName.toLowerCase());
  
  if (uploadedIcon) {
    return (
      <div 
        className="h-4 w-4 flex items-center justify-center 
                   /* 1. Force the SVG to fit the exact 16x16px box */
                   [&>svg]:h-4 [&>svg]:w-4 
                   /* 2. Force the internal SVG colors to follow the text color (currentColor) */
                   [&>svg]:fill-current [&>svg_path]:fill-current [&>svg_circle]:fill-current" 
        dangerouslySetInnerHTML={{ __html: uploadedIcon.svg_content }} 
      />
    );
  }

  // 2. Check if it's one of your new custom Figma icons
  const FigmaIcon = CustomIcons[iconName];
  if (FigmaIcon) return <FigmaIcon className="h-4 w-4" />;

  // 3. Check if it's a custom icon from your manual navigation map
  const CustomMapIcon = navigationIconMap[iconName.toLowerCase()];
  if (CustomMapIcon) return <div className="h-4 w-4 flex items-center justify-center">{CustomMapIcon}</div>;

  // 4. Check if it's a standard Lucide icon name from the library
  const LucideIcon = (icons as any)[iconName];
  if (LucideIcon) return <LucideIcon className="h-4 w-4" />;

  // 5. Final Fallback
  return <Sparkles className="h-4 w-4" />;
};

function NavIcon({
  isMobile,
  isActive,
  iconName,
  customIcons,
}: {
  isMobile: boolean;
  isActive: boolean;
  iconName: string;
  customIcons: CustomIconRow[];
}) {
  return (
    <div
      className={`flex h-4 w-4 shrink-0 items-center justify-center ${
        isMobile
          ? isActive
            ? 'text-white'
            : 'text-gray-600'
          : isActive
            ? 'text-white'
            : 'text-gray-600'
      }`}
    >
      <DynamicSidebarIcon iconName={iconName} customIcons={customIcons} />
    </div>
  );
}

const CustomAppLayout: React.FC = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, session } = useAuth();
  const { showShiftEndedOverlay } = useShiftAutoLogout();
  // A lead open in the (non-modal) carousel on RM PRD, OR a table-opened
  // lead whose "Lock lead modal" is enabled, blocks navigating to any OTHER
  // sidebar page until it's dispositioned, so the RM can't walk away
  // mid-lead. Doesn't touch Sparky/Notifications/Logout.
  const leadNavLocked = useLeadNavLocked();
  const isCurrentPage = (pageId: string) => location.pathname === `/app/${tenantSlug}/pages/${pageId}`;
  const navBlockedToast = () =>
    toast.warning('Finish this lead first', {
      description: 'Log a disposition before navigating away from RM PRD.',
    });
  const isPageBlocked = (pageId: string) => leadNavLocked && !isCurrentPage(pageId);
  const [pages, setPages] = useState<
    { id: string; name: string; icon_name: string; display_order?: number; config?: unknown }[]
  >([]);
  const [customIcons, setCustomIcons] = useState<CustomIconRow[]>([]);
  const [userRoleId, setUserRoleId] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [spoofBannerVisible, setSpoofBannerVisible] = useState(() => isSpoofing());
  const [spoofVersion, setSpoofVersion] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isMobile = useIsMobile();

  // Detect mobile landscape orientation smoothly
  const [isLandscape, setIsLandscape] = useState(() => 
    typeof window !== 'undefined' ? window.innerWidth > window.innerHeight : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobileLandscape = isMobile && isLandscape;
  const isTabletOrLandscapeMobile = isMobile || (typeof window !== 'undefined' && window.innerWidth < 1024 && isLandscape);

  const sidebarWidths = {
    expanded: 288,
    collapsed: 96,
  };

  // Expose sidebar offset for global overlays (e.g. Sparky on Unmannd top-left).
  useEffect(() => {
    const widthPx = isTabletOrLandscapeMobile
      ? 0
      : sidebarCollapsed
        ? sidebarWidths.collapsed
        : sidebarWidths.expanded;
    document.documentElement.style.setProperty('--app-sidebar-width', `${widthPx}px`);
    return () => {
      document.documentElement.style.removeProperty('--app-sidebar-width');
    };
  }, [isTabletOrLandscapeMobile, sidebarCollapsed]);

  const profileImage = user?.user_metadata?.picture || user?.user_metadata?.avatar_url || '';
  const spoofLabel = getSpoofUserLabel();
  const profileName = isSpoofing() && spoofLabel
    ? spoofLabel
    : user?.user_metadata?.full_name ||
      user?.user_metadata?.name ||
      user?.email?.split('@')[0] ||
      'User';

  const dataExtractedRef = useRef(false);

  // Resolve tenant/role from JWT (spoof token when spoofing)
  useEffect(() => {
    // Reset ref when user changes (e.g., on logout/login)
    if (!user) {
      dataExtractedRef.current = false;
      setTenantId(null);
      setUserRoleId(null);
      return;
    }

    const extractUserDataFromJWT = async () => {
      // Prevent redundant extraction when user object changes (e.g., on page focus)
      if (dataExtractedRef.current && tenantId && userRoleId) {
        return;
      }

      const token = await getEffectiveToken(session?.access_token ?? null);
      if (!token) {
        console.error('No session found');
        return;
      }

      // Extract tenant_id and role_id from JWT token (spoof or real)
      const extractedTenantId = getTenantIdFromJWT(token);
      const extractedRoleId = getRoleIdFromJWT(token);

      if (extractedTenantId && extractedRoleId) {
        console.log('Extracted from JWT - tenantId:', extractedTenantId, 'roleId:', extractedRoleId);
        setTenantId(extractedTenantId);
        setUserRoleId(extractedRoleId);
        localStorage.setItem('tenant_id', extractedTenantId);
        dataExtractedRef.current = true;
      } else {
        console.warn('Could not extract tenant_id or role_id from JWT');
        // Fallback: try to get from localStorage if available
        const cachedTenantId = localStorage.getItem('tenant_id');
        if (cachedTenantId) {
          setTenantId(cachedTenantId);
        }
      }
    };

    extractUserDataFromJWT();
  }, [user, session?.access_token, spoofVersion]);

  // Re-extract and update UI when spoof state changes (same tab or other tab)
  useEffect(() => {
    const onStorageChange = (e: StorageEvent) => {
      if (e.key === SPOOF_JWT_KEY || e.key === SPOOF_LABEL_KEY) {
        setSpoofBannerVisible(isSpoofing());
        dataExtractedRef.current = false;
        setSpoofVersion((v) => v + 1);
      }
    };
    const onSpoofChanged = () => {
      setSpoofBannerVisible(isSpoofing());
      dataExtractedRef.current = false;
      setSpoofVersion((v) => v + 1);
    };
    window.addEventListener('storage', onStorageChange);
    window.addEventListener(SPOOF_CHANGED_EVENT, onSpoofChanged);
    return () => {
      window.removeEventListener('storage', onStorageChange);
      window.removeEventListener(SPOOF_CHANGED_EVENT, onSpoofChanged);
    };
  }, []);

  // Step 2: Fetch pages AND custom icons
  useEffect(() => {
    const fetchPagesAndIcons = async () => {
      if (!tenantId || !userRoleId) return;

      try {
        const iconResponse = await apiClient.get('/pages/custom-icons/');
        setCustomIcons(iconResponse.data || []);
      } catch (err) {
        console.error('Failed to fetch custom icons:', err);
      }

      const token = await getEffectiveToken(session?.access_token ?? null);

      if (token) {
        // Pages are saved through the backend, so read them back from it (Supabase REST is only the fallback).
        try {
          const pagesData = await fetchPagesForRole(tenantId, userRoleId, token);
          const sortedPages = (pagesData || []).sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0));
          setPages(sortedPages);
        } catch (err) {
          console.error('Pages fetch error:', err);
          toast.error('Failed to load pages');
          setPages([]);
        }
        return;
      }

      const { data: pagesData, error } = await supabase
        .from('pages')
        .select('id, name, display_order, icon_name, config')
        .eq('tenant_id', tenantId)
        .eq('role', userRoleId)
        .eq('is_deleted', false)
        .order('display_order', { ascending: true });

      if (error) {
        toast.error('Failed to load pages');
        const pg = error as { message?: string; code?: string; details?: string; hint?: string };
        console.error(
          'Pages fetch error:',
          pg.message ?? 'unknown',
          pg.code ? `code=${pg.code}` : '',
          pg.details || pg.hint || ''
        );
      } else {
        setPages(pagesData || []);
      }
    };

    fetchPagesAndIcons();
  }, [tenantId, userRoleId, session?.access_token, spoofVersion]);

  const handleStopSpoofing = () => {
    try {
      clearSpoofLocalStorage();
      dispatchSpoofChanged();
      dataExtractedRef.current = false;
      setTenantId(null);
      setUserRoleId(null);
      navigate('/');
    } catch (err) {
      console.error('Failed to stop spoofing:', err);
    }
  };

  // Unmannd / procurement apps: navy active nav to match procurement table headers.
  // CRM tenants (e.g. praja) keep black.
  const isUnmanndApp = (() => {
    const slug = String(tenantSlug || '').toLowerCase();
    if (/unman+d/.test(slug)) return true;
    const names = pages.map((p) => String(p.name || '').toLowerCase());
    const requestPages = names.filter((n) =>
      /request|procurement|pending approval|vendor identified/.test(n)
    );
    return requestPages.length >= 2;
  })();
  const allLeadsPath = useMemo(() => {
    if (!tenantSlug || !pages.length) return null;
    // Exact name only — `includes("all leads")` can bind the wrong page
    // (e.g. "Assigned all leads overview").
    const allLeadsPage = pages.find(
      (page) => String(page.name || '').trim().toLowerCase() === 'all leads',
    );
    if (!allLeadsPage) return null;
    return `/app/${tenantSlug}/pages/${allLeadsPage.id}`;
  }, [tenantSlug, pages]);

  useEffect(() => {
    if (allLeadsPath) {
      registerAllLeadsPath(allLeadsPath);
    } else {
      // Tenant switch / no All Leads page — don't keep a stale path.
      clearRegisteredAllLeadsPath();
    }
    // Do not clear on unmount: Dashboard Navbar still needs the registered path
    // after leaving CustomAppLayout. Logout clears via the notification provider.
  }, [allLeadsPath]);
  const activeNavClass = isUnmanndApp
    ? 'bg-[linear-gradient(0deg,#1A44A1,#1A44A1)] text-white'
    : 'bg-black text-white';
  const activeNavClassMobile = isUnmanndApp
    ? 'bg-[linear-gradient(0deg,#1A44A1,#1A44A1)] text-white shadow-sm'
    : 'bg-black text-white shadow-sm';
  const brandLogoSrc = isUnmanndApp ? '/pyro-ai-logo.png' : '/fire-logo.png';
  const brandLogoAlt = isUnmanndApp ? 'Pyro.ai' : 'Pyro';
  const navItemPad = isUnmanndApp
    ? { collapsed: 'justify-center px-0 py-1.5', expanded: 'gap-2.5 px-2.5 py-1.5' }
    : { collapsed: 'justify-center px-0 py-2', expanded: 'gap-3 px-3 py-2' };
  const sidebarNav = useMemo(() => buildSidebarNav(pages), [pages]);
  const renderGroupIcon = (iconName: string, isActive: boolean) => (
    <span className={isActive ? 'text-white' : 'text-gray-600'}>
      <DynamicSidebarIcon iconName={iconName} customIcons={customIcons} />
    </span>
  );

  const renderNavLinks = (opts: { collapsed: boolean; onNavigate?: () => void }) => (
    <>
      {sidebarNav.map((item) => {
        if (item.kind === 'group') {
          return (
            <SidebarGroupFlyout
              key={`group-${item.name}`}
              name={item.name}
              sections={item.sections}
              tenantSlug={tenantSlug}
              collapsed={opts.collapsed}
              inline
              activeClass={activeNavClassMobile}
              triggerPadClass={opts.collapsed ? navItemPad.collapsed : navItemPad.expanded}
              renderIcon={renderGroupIcon}
              onNavigate={opts.onNavigate}
              isPageBlocked={isPageBlocked}
              onBlockedNavigate={navBlockedToast}
            />
          );
        }
        const { page } = item;
        const blocked = isPageBlocked(page.id);
        return (
          <NavLink
            key={page.id}
            to={`/app/${tenantSlug}/pages/${page.id}`}
            onClick={(e) => {
              if (blocked) {
                e.preventDefault();
                navBlockedToast();
                return;
              }
              opts.onNavigate?.();
            }}
            aria-disabled={blocked}
            className={({ isActive }) =>
              `flex items-center rounded-sm font-medium transition ${
                opts.collapsed ? navItemPad.collapsed : navItemPad.expanded
              } ${isMobileLandscape ? 'py-0 px-0.5 text-[8px] gap-1 h-4' : 'text-sm'} ${
                blocked ? 'opacity-40 cursor-not-allowed' : ''
              } ${
                isTabletOrLandscapeMobile
                  ? isActive
                    ? activeNavClassMobile
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : isActive
                    ? activeNavClass
                    : 'text-gray-700 hover:bg-gray-50'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <NavIcon
                  isMobile={isTabletOrLandscapeMobile}
                  isActive={isActive}
                  iconName={page.icon_name}
                  customIcons={customIcons}
                />
                {!opts.collapsed && <span className="truncate">{page.name}</span>}
              </>
            )}
          </NavLink>
        );
      })}
    </>
  );

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      // Use the centralized logout function
      await logout();
      
      // Close the logout dropdown
      setLogoutOpen(false);
      
      // Navigate to login page
      navigate(`/app/${tenantSlug}/login`);
      
    } catch (error) {
      console.error('Logout navigation error:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const mainMarginLeft = isTabletOrLandscapeMobile
    ? 0
    : sidebarCollapsed
      ? sidebarWidths.collapsed
      : sidebarWidths.expanded;

  return (
    <div
      className="flex h-svh w-full overflow-hidden"
      style={{
        ['--sidebar-width' as string]: `${sidebarCollapsed ? sidebarWidths.collapsed : sidebarWidths.expanded}px`
      }}
    >
      {isTabletOrLandscapeMobile ? (
        <>
          <header className="fixed left-0 right-0 top-0 z-50 flex h-12 items-center justify-between bg-black px-3 text-white">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="flex h-8 w-8 items-center justify-center rounded-lg"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <img src={brandLogoSrc} alt={brandLogoAlt} className="h-6 object-contain" />
            <div className="w-8" />
          </header>
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent side="left" className={`flex flex-col bg-white p-0 ${isMobileLandscape ? 'w-[100px]' : 'w-[min(100vw-2rem,320px)]'}`}>
              <div className={`flex items-center gap-1 border-b ${isMobileLandscape ? 'px-1 py-0.5' : 'px-4 py-4'}`}>
                <button
                  type="button"
                  onClick={() => setMobileNavOpen(false)}
                  className={`flex items-center justify-center rounded border ${isMobileLandscape ? 'h-3.5 w-3.5' : 'h-9 w-9'}`}
                  aria-label="Close menu"
                >
                  <ChevronLeft className={isMobileLandscape ? 'h-2 w-2' : 'h-5 w-5'} />
                </button>
                <img src={brandLogoSrc} alt={brandLogoAlt} className={isMobileLandscape ? 'h-3 object-contain' : 'h-8 object-contain'} />
              </div>
              <nav
                className={`flex-1 overflow-y-auto ${isMobileLandscape ? 'p-0.5 space-y-0.5' : 'p-3 space-y-2'}`}
              >
                {renderNavLinks({ collapsed: false, onNavigate: () => setMobileNavOpen(false) })}
              </nav>
              <div className={`border-t ${isMobileLandscape ? 'p-0.5 space-y-0.5' : 'p-3 space-y-2'}`}>
                <SparkySidebarButton
                  placePanelAway
                  onToggle={() => setMobileNavOpen(false)}
                />
                <LeadCalledBackNotificationsMenu
                  variant="sidebar"
                  allLeadsPath={allLeadsPath}
                  className="rounded-xl bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700"
                />
                <div className="flex items-center gap-3 rounded-xl px-3 py-2">
                  <img
                    src={profileImage || '/default-avatar.png'}
                    alt={profileName}
                    className={`rounded-full object-cover ${isMobileLandscape ? 'h-3 w-3' : 'h-10 w-10'}`}
                  />
                  <p className={`truncate font-semibold text-gray-900 ${isMobileLandscape ? 'text-[8px]' : 'text-sm'}`}>{profileName}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setMobileNavOpen(false);
                    void handleLogout();
                  }}
                  disabled={isLoggingOut}
                  className={`flex w-full items-center gap-1 rounded border font-medium text-gray-600 ${isMobileLandscape ? 'px-1 py-0.5 text-[8px]' : 'px-3 py-2 text-sm'}`}
                >
                  <LogOut className={isMobileLandscape ? 'h-2 w-2' : 'h-4 w-4'} />
                  {isLoggingOut ? 'Logging out...' : 'Logout'}
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </>
      ) : null}

      {/* Desktop sidebar */}
      <div
        className={`fixed left-0 top-0 h-full bg-white transition-all duration-200 ${isTabletOrLandscapeMobile ? 'hidden' : ''}`}
        style={{ width: sidebarCollapsed ? sidebarWidths.collapsed : sidebarWidths.expanded }}
      >
        <aside className="relative flex h-full flex-col border-r bg-white">
            <div
              className={`flex items-center flex-shrink-0 w-full ${
                sidebarCollapsed ? 'justify-center px-0' : 'justify-between px-4'
              } ${isUnmanndApp ? 'pt-3 pb-2' : 'pt-6 pb-4'}`}
            >
            {sidebarCollapsed ? (
              <button
                onClick={() => setSidebarCollapsed(false)}
                className="flex h-8 w-8 items-center justify-center transition hover:bg-gray-100 cursor-pointer rounded-lg"
                aria-label="Expand sidebar"
              >
                <img src="/menu_close.svg" alt="Menu" className="h-5 w-5" />
              </button>
            ) : (
              <>
                <div className="flex items-center justify-start flex-1">
                  <img
                    src={brandLogoSrc}
                    alt={brandLogoAlt}
                    className={`h-auto w-auto object-contain ${isUnmanndApp ? 'max-h-9' : 'max-h-12'}`}
                  />
                </div>
                <button
                  onClick={() => setSidebarCollapsed(true)}
                  className="flex h-8 w-8 items-center justify-center transition hover:bg-gray-100 cursor-pointer rounded-lg"
                  aria-label="Collapse sidebar"
                >
                  <img src="/menu_open.svg" alt="Close Menu" className="h-5 w-5" />
                </button>
              </>
            )}
          </div>

          <nav
            className={`flex-1 overflow-y-auto ${isUnmanndApp ? 'space-y-1 py-1' : 'space-y-2 py-2'} ${
              sidebarCollapsed ? 'px-2' : 'px-3'
            }`}
          >
            {sidebarNav.map((item) => {
              if (item.kind === 'group') {
                return (
                  <SidebarGroupFlyout
                    key={`group-${item.name}`}
                    name={item.name}
                    sections={item.sections}
                    tenantSlug={tenantSlug}
                    collapsed={sidebarCollapsed}
                    inline={!sidebarCollapsed}
                    activeClass={activeNavClass}
                    triggerPadClass={sidebarCollapsed ? navItemPad.collapsed : navItemPad.expanded}
                    renderIcon={renderGroupIcon}
                    isPageBlocked={isPageBlocked}
                    onBlockedNavigate={navBlockedToast}
                  />
                );
              }
              const { page } = item;
              const blocked = isPageBlocked(page.id);
              return (
                <NavLink
                  key={page.id}
                  to={`/app/${tenantSlug}/pages/${page.id}`}
                  onClick={(e) => {
                    if (!blocked) return;
                    e.preventDefault();
                    navBlockedToast();
                  }}
                  aria-disabled={blocked}
                  className={({ isActive }) =>
                    `flex items-center rounded-xl ${
                      sidebarCollapsed ? navItemPad.collapsed : navItemPad.expanded
                    } text-sm font-medium transition ${blocked ? 'opacity-40 cursor-not-allowed' : ''} ${
                      isActive
                        ? activeNavClass
                        : 'text-gray-700 hover:bg-gray-50'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <div className={`flex h-8 w-8 items-center justify-center rounded-full ${isActive ? 'bg-transparent text-white' : 'bg-transparent text-gray-600'}`}>
                        {/* Call the helper with the icon_name from your database */}
                        <DynamicSidebarIcon iconName={page.icon_name} customIcons={customIcons} />
                      </div>
                      {!sidebarCollapsed && <span>{page.name}</span>}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          <div
            className={`flex-shrink-0 bg-white border-t ${
              isUnmanndApp ? 'py-2.5 space-y-2' : 'py-4 space-y-3'
            } ${sidebarCollapsed ? 'px-2' : 'px-3'}`}
          >
            <SparkySidebarButton collapsed={sidebarCollapsed} />
            <LeadCalledBackNotificationsMenu
              variant={sidebarCollapsed ? "sidebar-collapsed" : "sidebar"}
              allLeadsPath={allLeadsPath}
            />

            <div className={`border-t space-y-2 ${isUnmanndApp ? 'pt-2.5' : 'pt-4'}`}>
              <div className={`flex items-center rounded-xl px-3 py-2 ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
              {sidebarCollapsed ? (
                <img
                  src={profileImage || '/default-avatar.png'}
                  alt={profileName}
                  className={`${isUnmanndApp ? 'h-8 w-8' : 'h-10 w-10'} rounded-full object-cover`}
                />
              ) : (
                <div className="flex items-center gap-3">
                  <img
                    src={profileImage || '/default-avatar.png'}
                    alt={profileName}
                    className={`${isUnmanndApp ? 'h-8 w-8' : 'h-10 w-10'} rounded-full object-cover`}
                  />
                  <p className="text-sm font-semibold text-gray-900">{profileName}</p>
                </div>
              )}
            </div>
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-sm font-medium transition ${
                sidebarCollapsed
                  ? 'justify-center'
                  : ''
              } ${
                isLoggingOut
                  ? 'opacity-50 cursor-not-allowed border-transparent text-gray-500'
                  : 'border-transparent text-gray-600 hover:border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
                isLoggingOut
                  ? 'bg-gray-100 text-gray-500'
                  : 'bg-gray-100 text-gray-500'
              }`}>
                <LogOut className="h-4 w-4" />
              </div>
              {!sidebarCollapsed && (
                <span>{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
              )}
            </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Main Content — sidebar is fixed, so width must subtract its offset (margin alone overflows). */}
      <main
        className="min-w-0 h-full overflow-x-hidden overflow-y-auto bg-white transition-all duration-200"
        style={{
          marginLeft: mainMarginLeft,
          width: `calc(100% - ${mainMarginLeft}px)`,
          maxWidth: `calc(100% - ${mainMarginLeft}px)`,
          paddingTop: isTabletOrLandscapeMobile ? 48 : 0,
        }}
      >
        {spoofBannerVisible && spoofLabel && (
          <div className="w-full bg-yellow-300 text-black text-xs px-4 py-1 flex items-center justify-between shrink-0">
            <span className="truncate">
              Spoofing as <span className="font-semibold">{profileName}</span>
            </span>
            <button
              type="button"
              onClick={handleStopSpoofing}
              className="ml-4 rounded border border-black/40 px-2 py-0.5 text-xs font-medium hover:bg-black hover:text-amber-300"
            >
              Stop spoofing
            </button>
          </div>
        )}
        <div
          className={
            isUnmanndApp
              ? 'flex h-full min-h-0 w-full flex-col'
              : 'min-h-screen w-full'
          }
          style={
            isUnmanndApp
              ? { fontFamily: "Helvetica, 'Helvetica Neue', Arial, sans-serif" }
              : undefined
          }
        >
          <Outlet context={{ tenantId, userRoleId, pages, isUnmanndApp }} />
        </div>
      </main>
      {showShiftEndedOverlay && <ShiftEndedOverlay />}
    </div>
  );
};

export default CustomAppLayout;